import type { ClientGame, ClientGameCtx } from "./index";
import { sound } from "../sound";

const ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];
const RANK: Record<string, number> = { G: 3, Y: 2, B: 1, "": 0 };

export class WordDuelClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private meGrid!: HTMLDivElement;
  private rivalGrid!: HTMLDivElement;
  private statusEl!: HTMLDivElement;
  private rivalEl!: HTMLDivElement;
  private kbEl!: HTMLDivElement;
  private msgEl!: HTMLDivElement;
  private current = "";
  private onKey = (e: KeyboardEvent) => this.handleKey(e.key);

  private get wd() { return this.ctx.room.state.wordduel; }
  private get solo() { return !!this.wd.solo; }
  private get len() { return this.wd.wordLen || 5; }
  private get max() { return this.wd.maxGuesses || 6; }
  private get mine() { return this.wd.sides?.[this.ctx.mySlot]; }
  private get theirs() { return this.wd.sides?.[this.ctx.mySlot === 0 ? 1 : 0]; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();

    const wrap = document.createElement("div");
    wrap.className = "wd-wrap";

    const top = document.createElement("div");
    top.className = "wd-top";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "wd-status";
    this.rivalEl = document.createElement("div");
    this.rivalEl.className = "wd-rival-label";
    this.rivalGrid = document.createElement("div");
    this.rivalGrid.className = "wd-grid rival";
    const rivalBox = document.createElement("div");
    rivalBox.className = "wd-rival-box";
    rivalBox.append(this.rivalEl, this.rivalGrid);
    top.append(this.statusEl, rivalBox);

    this.meGrid = document.createElement("div");
    this.meGrid.className = "wd-grid me";

    this.msgEl = document.createElement("div");
    this.msgEl.className = "wd-msg";

    this.kbEl = document.createElement("div");
    this.kbEl.className = "wd-kb";
    this.buildKeyboard();

    wrap.append(top, this.meGrid, this.msgEl, this.kbEl);
    ctx.root.appendChild(wrap);

    if (this.solo) {
      this.rivalEl.style.display = "none";
      this.rivalGrid.style.display = "none";
    }
    ctx.room.onMessage("wd-solved", (m: any) => {
      sound.levelUp();
      this.current = "";
      this.flashMsg(`✓ ${String(m?.word || "").toUpperCase()} in ${m?.guesses} — next word!`, 1600, true);
      this.renderMe();
      this.renderKeyboard();
    });
    ctx.room.onMessage("wd-invalid", (m: any) => {
      if (m?.slot !== this.ctx.mySlot) return;
      this.flashMsg("Not a real word");
    });

    window.addEventListener("keydown", this.onKey);
    this.sync();
  }

  unmount() {
    window.removeEventListener("keydown", this.onKey);
  }

  private buildKeyboard() {
    this.kbEl.innerHTML = "";
    ROWS.forEach((row, ri) => {
      const r = document.createElement("div");
      r.className = "wd-krow";
      if (ri === 2) r.appendChild(this.key("ENTER", "enter wide"));
      for (const ch of row) r.appendChild(this.key(ch, ""));
      if (ri === 2) r.appendChild(this.key("⌫", "back wide"));
      this.kbEl.appendChild(r);
    });
  }

  private key(label: string, extra: string) {
    const b = document.createElement("button");
    b.className = `wd-key ${extra}`;
    b.textContent = label;
    b.dataset.k = label.toLowerCase();
    b.onclick = () => {
      if (label === "ENTER") this.handleKey("Enter");
      else if (label === "⌫") this.handleKey("Backspace");
      else this.handleKey(label);
    };
    return b;
  }

  private handleKey(k: string) {
    if (this.ctx.room.state.phase !== "playing") return;
    const me = this.mine;
    if (!me || me.solved || me.out) return;

    if (k === "Enter") {
      if (this.current.length !== this.len) { this.flashMsg("Not enough letters"); return; }
      this.ctx.room.send("guess", { word: this.current });
      sound.submit();
      this.current = "";
      this.renderMe();
      return;
    }
    if (k === "Backspace") {
      this.current = this.current.slice(0, -1);
      sound.key();
      this.renderMe();
      return;
    }
    if (/^[a-zA-Z]$/.test(k) && this.current.length < this.len) {
      this.current += k.toLowerCase();
      sound.key();
      this.renderMe();
    }
  }

  private flashMsg(t: string, ms = 1200, good = false) {
    this.msgEl.textContent = t;
    this.msgEl.classList.toggle("good", good);
    this.msgEl.classList.add("show");
    setTimeout(() => this.msgEl.classList.remove("show"), ms);
  }

  sync() {
    this.renderMe();
    this.renderRival();
    this.renderKeyboard();

    const me = this.mine;
    const them = this.theirs;
    const phase = this.ctx.room.state.phase;
    if (phase === "results") { this.statusEl.textContent = ""; return; }

    if (this.solo) {
      this.statusEl.textContent =
        `Word ${(this.wd.soloWords ?? 0) + 1} · ${this.wd.soloScore ?? 0} pts · guess ${(me?.rows.length ?? 0) + 1}/${this.max}`;
      return;
    }

    if (me?.solved) this.statusEl.textContent = `Solved in ${me.rows.length}! Waiting…`;
    else if (me?.out) this.statusEl.textContent = "Out of guesses — hope they miss!";
    else this.statusEl.textContent = `Guess ${(me?.rows.length ?? 0) + 1} of ${this.max}`;

    const tn = them ? them.rows.length : 0;
    this.rivalEl.textContent = them?.solved
      ? `RIVAL SOLVED (${tn})`
      : them?.out
      ? "RIVAL OUT"
      : `RIVAL ${tn}/${this.max}`;
  }

  private renderMe() {
    const me = this.mine;
    const rows: { word: string; pattern: string }[] = [];
    if (me) me.rows.forEach((r: any) => rows.push({ word: r.word, pattern: r.pattern }));
    this.paintGrid(this.meGrid, rows, this.current, true);
  }

  private renderRival() {
    const them = this.theirs;
    const rows: { word: string; pattern: string }[] = [];
    if (them) them.rows.forEach((r: any) => rows.push({ word: r.word, pattern: r.pattern }));
    this.paintGrid(this.rivalGrid, rows, "", false);
  }

  private paintGrid(
    grid: HTMLDivElement,
    rows: { word: string; pattern: string }[],
    typing: string,
    showLetters: boolean,
  ) {
    grid.innerHTML = "";
    grid.style.setProperty("--cols", String(this.len));
    const me = grid === this.meGrid ? this.mine : this.theirs;
    const done = !!(me?.solved || me?.out);
    for (let rr = 0; rr < this.max; rr++) {
      const row = document.createElement("div");
      row.className = "wd-row";
      const filled = rows[rr];
      const isTypingRow = !filled && rr === rows.length && !done;
      for (let cc = 0; cc < this.len; cc++) {
        const tile = document.createElement("div");
        tile.className = "wd-tile";
        if (filled) {
          const p = filled.pattern[cc] || "B";
          tile.classList.add(p === "G" ? "g" : p === "Y" ? "y" : "b");
          if (showLetters) tile.textContent = (filled.word[cc] || "").toUpperCase();
        } else if (isTypingRow && showLetters) {
          tile.textContent = (typing[cc] || "").toUpperCase();
          if (typing[cc]) tile.classList.add("typed");
        }
        row.appendChild(tile);
      }
      grid.appendChild(row);
    }
  }

  private renderKeyboard() {
    const best: Record<string, string> = {};
    const me = this.mine;
    if (me) {
      me.rows.forEach((r: any) => {
        for (let i = 0; i < r.word.length; i++) {
          const ch = r.word[i];
          const st = r.pattern[i] || "B";
          if ((RANK[st] ?? 0) > (RANK[best[ch] || ""] ?? 0)) best[ch] = st;
        }
      });
    }
    this.kbEl.querySelectorAll<HTMLButtonElement>(".wd-key").forEach((b) => {
      const k = b.dataset.k || "";
      b.classList.remove("g", "y", "b");
      const st = best[k];
      if (st === "G") b.classList.add("g");
      else if (st === "Y") b.classList.add("y");
      else if (st === "B") b.classList.add("b");
    });
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const wd = ctx.room.state.wordduel;
    const wrap = document.createElement("div");
    wrap.className = "wd-wrap results-mini";
    if (wd.solo) {
      const d = document.createElement("div");
      d.className = "arc-final";
      d.innerHTML =
        `<div class="score-big">${(wd.soloScore ?? 0).toLocaleString()}</div>` +
        `<div class="arc-note">${wd.soloWords ?? 0} word${wd.soloWords === 1 ? "" : "s"} solved · missed <b>${(wd.answer || "").toUpperCase()}</b></div>`;
      wrap.appendChild(d);
      return wrap;
    }
    const word = document.createElement("div");
    word.className = "wd-answer";
    word.textContent = (wd.answer || "").toUpperCase();
    const label = document.createElement("div");
    label.className = "wd-status";
    label.textContent = "the word was";
    wrap.append(label, word);
    return wrap;
  }
}
